import { useState } from 'react';
import { Link } from 'react-router';
import { ApiError, api, errorMessage } from '../../shared/api/client';
import type { MultimediaDevice } from '../../shared/api/types';
import { Alert, Button, Card } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';
import { useMe } from '../session/SessionProvider';
import { inDesktopApp } from './screens';

const when = (iso: string) => new Date(iso).toLocaleString('es', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

/**
 * Equipo de multimedia: la computadora de proyección de la iglesia. Se vincula una vez desde una
 * cuenta que opera Presentaciones; la sesión personal se cierra y la computadora queda entrando sola.
 * Cada iglesia tiene un límite (1): vincular otra pide reemplazar la anterior.
 */
export function DeviceCard() {
  const me = useMe();
  const churchId = me.activeChurchId;
  const url = `/security/churches/${churchId}/devices`;
  const devices = useAsync((signal) => (churchId ? api.get<{ limit: number; items: MultimediaDevice[] }>(url, signal) : Promise.reject(new Error('Sin contexto'))), [url]);
  const [name, setName] = useState(inDesktopApp() ? 'Computadora de proyección' : '');
  const [linking, setLinking] = useState(false);
  const [conflict, setConflict] = useState<MultimediaDevice[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!churchId || me.isDevice) return null;

  async function link(replace: boolean) {
    setBusy(true);
    setError(null);
    try {
      await api.post(url, { name, replace });
      // La sesión ya es la del equipo: se recarga para entrar con ella.
      window.location.assign('/app/presentaciones');
    } catch (e) {
      const details = e instanceof ApiError ? (e.details as { reason?: string; devices?: MultimediaDevice[] } | undefined) : undefined;
      if (details?.reason === 'device_limit') setConflict(details.devices ?? []);
      else setError(errorMessage(e));
      setBusy(false);
    }
  }

  async function unlink(d: MultimediaDevice) {
    if (!window.confirm(`¿Desvincular «${d.name}»? Esa computadora dejará de entrar sola.`)) return;
    try {
      await api.delete(`${url}/${d.id}`);
      devices.reload();
    } catch (e) {
      setError(errorMessage(e));
    }
  }

  const list = devices.status === 'ready' ? devices.data.items : [];
  return (
    <Card title="Equipo de multimedia">
      <p className="small muted">
        Vincula la computadora de proyección: entrará sola con Proyectar, Cantos, Multimedia y Biblia, aunque cierres tu sesión. Nada tuyo queda abierto en ella.
        {devices.status === 'ready' ? ` La iglesia puede tener ${devices.data.limit === 1 ? 'un equipo' : `${devices.data.limit} equipos`}.` : ''}
      </p>
      {inDesktopApp() ? null : (
        <p className="small">
          Para que cada pantalla salga sola a su monitor, instala la app desde <Link to="/app/multimedia">Multimedia</Link>.
        </p>
      )}
      {error ? <Alert>{error}</Alert> : null}
      {list.length > 0 ? (
        <ul className="list">
          {list.map((d) => (
            <li key={d.id} className="list__item device-row">
              <span>
                <strong>{d.name}</strong>
                <span className="muted small"> · vinculado {when(d.linkedAt)} · última vez {when(d.lastSeen)}</span>
              </span>
              <button type="button" className="link-button" onClick={() => void unlink(d)}>
                Desvincular
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      {conflict ? (
        <Alert tone="info">
          <p>
            Ya está vinculado{conflict.length === 1 ? '' : 's'} {conflict.map((d) => `«${d.name}»`).join(', ')}. Si vinculas esta computadora, {conflict.length === 1 ? 'esa se desconecta' : 'la más antigua se desconecta'} al instante.
          </p>
          <div className="actions">
            <Button busy={busy} onClick={() => void link(true)}>
              Reemplazar y vincular esta
            </Button>
            <Button type="button" variant="ghost" onClick={() => setConflict(null)}>
              Cancelar
            </Button>
          </div>
        </Alert>
      ) : linking ? (
        <form
          className="form"
          onSubmit={(e) => {
            e.preventDefault();
            void link(false);
          }}
        >
          <div className="field">
            <label htmlFor="device-name">Nombre de esta computadora</label>
            <input id="device-name" value={name} maxLength={60} required placeholder="Ej. Cabina de proyección" onChange={(e) => setName(e.target.value)} />
          </div>
          <p className="small muted">Al vincularla se cierra tu sesión en esta computadora.</p>
          <div className="actions">
            <Button type="submit" busy={busy}>
              Vincular esta computadora
            </Button>
            <Button type="button" variant="ghost" onClick={() => setLinking(false)}>
              Cancelar
            </Button>
          </div>
        </form>
      ) : (
        <Button variant="secondary" onClick={() => setLinking(true)}>
          Vincular esta computadora
        </Button>
      )}
    </Card>
  );
}
